import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trophy, Medal, Star, Lock, CheckCircle, Check, Sparkle, TrendUp, PencilSimple, Gift, CurrencyDollar, Crown, ArrowCounterClockwise, SlidersHorizontal } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import RewardsStructure from "./RewardsStructure";
import Leaderboard from "./Leaderboard";

const LEVEL_TONE = { Bronze: "#B08D57", Silver: "#8A8A8A", Gold: "#8a6d0a", Platinum: "#4A0000", Elite: "#4A0000" };
const MEMBER_IMG = "https://images.unsplash.com/photo-1760611656071-a8bef0578874?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ROLE_TABS = [["employee", "Employees"], ["photographer", "Photographers"], ["agent", "Agents"], ["pilot", "Pilots"]];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.05, delayChildren: 0.04 } } };
const fadeUp = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 260, damping: 22 } } };
const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function AnimatedNumber({ value, decimals = 0, className }) {
  const [n, setN] = useState(reduced() ? value : 0);
  const raf = useRef();
  useEffect(() => {
    if (reduced()) { setN(value); return; }
    const from = 0, dur = 850, start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(from + (value - from) * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);
  return <span className={className}>{decimals ? n.toFixed(decimals) : Math.round(n).toLocaleString()}</span>;
}

function ProgressRing({ value = 0, size = 96, stroke = 8, color = "#E9C77E", track = "rgba(253,251,247,0.15)", children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: reduced() ? off : c }} animate={{ strokeDashoffset: off }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.15 }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

function MemberRewards() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [justRedeemed, setJustRedeemed] = useState(null);
  const [selectedCreditKey, setSelectedCreditKey] = useState(null);
  const load = () => client.get("/rewards/me").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-[#5C5A58]">Loading…</p>;
  const tone = LEVEL_TONE[d.level.name] || "#B08D57";

  const doRedeem = async (item) => {
    if (!item?.affordable || redeeming) return;
    setRedeeming(item.key);
    try {
      const r = await client.post("/rewards/redeem", { item_key: item.key });
      setD(r.data);
      setConfirmItem(null);
      setJustRedeemed(item.key);
      setTimeout(() => setJustRedeemed(null), 1500);
      toast.success(item.type === "penthouse"
        ? "🔓 Penthouse Partner unlocked — reload to see your new perks."
        : `${item.label} added — it auto-applies to your next shoot invoice.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't redeem right now."); }
    finally { setRedeeming(null); }
  };

  return (
    <motion.div className="space-y-8" data-testid="rewards-panel" variants={container} initial="hidden" animate="show">
      {/* Tier hero — luxury members card */}
      <motion.div variants={fadeUp} data-testid="rewards-level-card"
        className="relative overflow-hidden rounded-[5px] p-8 shadow-[0_10px_40px_rgba(74,0,0,0.28)]"
        style={{ background: "#4A0000" }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.13]" style={{ backgroundImage: `url(${MEMBER_IMG})` }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(130% 120% at 100% 0%, rgba(233,199,126,0.28), transparent 52%)" }} />
        <div className="noise-overlay" />

        <div className="relative flex items-start justify-between flex-wrap gap-8">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#E9C77E]/80 flex items-center gap-1.5">
              <Sparkle size={13} weight="fill" />Reelty Rewards · Members Club
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <div className="w-11 h-11 rounded-full flex items-center justify-center ring-1 ring-[#E9C77E]/40" style={{ background: "rgba(233,199,126,0.14)" }}>
                <Trophy size={24} weight="fill" className="text-[#E9C77E]" />
              </div>
              <p className="font-display text-4xl sm:text-5xl leading-none text-[#FBFAF7]" data-testid="rewards-level-name">{d.level.name}</p>
            </div>
            <div className="flex items-end gap-2 mt-5">
              <span data-testid="rewards-points" className="font-display text-4xl text-[#E9C77E] leading-none">
                <AnimatedNumber value={d.points} />
              </span>
              <span className="text-sm text-[#FBFAF7]/60 pb-0.5">points · {d.earned_count}/{d.achievement_total} achievements</span>
            </div>
            {d.cash_value != null && (
              <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="rewards-cash-value">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBFAF7]/10 border border-[#E9C77E]/30 px-3 py-1 text-sm text-[#FBFAF7]">
                  <CurrencyDollar size={14} weight="duotone" className="text-[#E9C77E]" />Worth <span className="font-semibold text-[#E9C77E]">${d.cash_value.toFixed(2)}</span>
                  <span className="text-[#FBFAF7]/45 text-xs">· {d.points_per_dollar} pts = $1</span>
                </span>
                {d.credit_balance > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#8FBF9F]/15 border border-[#8FBF9F]/35 px-3 py-1 text-sm text-[#CDE7D4]">
                    <CheckCircle size={14} weight="fill" />${d.credit_balance.toFixed(2)} credit ready
                  </span>
                )}
              </div>
            )}
          </div>
          <ProgressRing value={d.level.next ? d.level.progress : 100}>
            {d.level.next ? (
              <>
                <span className="font-display text-xl text-[#FBFAF7] leading-none"><AnimatedNumber value={d.level.to_next} /></span>
                <span className="text-[10px] uppercase tracking-wider text-[#E9C77E]/80 mt-1">to {d.level.next}</span>
                <div className="mt-0.5" data-testid="rewards-progress" style={{ width: 1, height: 1 }} />
              </>
            ) : (
              <>
                <Crown size={22} weight="fill" className="text-[#E9C77E]" />
                <span className="text-[10px] uppercase tracking-wider text-[#E9C77E]/80 mt-1">Top tier</span>
                <div data-testid="rewards-progress" style={{ width: 1, height: 1 }} />
              </>
            )}
          </ProgressRing>
        </div>
      </motion.div>

      {/* On-time delivery streak (crew) */}
      {(user.role === "photographer" || user.role === "pilot" || user.role === "dual") && (
        <motion.div variants={fadeUp} data-testid="rewards-streak"
          className="flex items-center gap-4 rounded-[8px] border border-[#B08D57]/25 bg-gradient-to-r from-[#B08D57]/10 to-transparent px-5 py-4">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-[#4A0000] text-2xl shrink-0">🔥</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#2C2626]">
              {d.delivery_streak > 0
                ? <>You're on a <span className="text-[#4A0000]">{d.delivery_streak}-shoot</span> on-time delivery streak</>
                : "Start an on-time delivery streak"}
            </p>
            <p className="text-xs text-[#6B6660] mt-0.5">
              Deliver on time in a row for bonus points at 3, 5, 10, 25, 50 & 100.
              {d.delivery_streak_best > 0 && <> · Best streak: {d.delivery_streak_best}</>}
            </p>
          </div>
        </motion.div>
      )}

      {/* Redemption store (roles with a live store) */}
      {d.store && d.store.length > 0 && (
        <motion.div variants={fadeUp} className="space-y-4" data-testid="rewards-store">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58] flex items-center gap-1.5"><Gift size={16} weight="duotone" className="text-[#B08D57]" />Redeem your points</p>
            {d.credit_balance > 0 && <span className="text-xs text-[#2E4F3B] bg-[#2E4F3B]/10 border border-[#2E4F3B]/25 rounded-full px-3 py-1" data-testid="rewards-credit-chip">${d.credit_balance.toFixed(2)} credit auto-applies to your next shoot</span>}
          </div>
          {(() => {
            const creditItems = (d.store || []).filter((it) => it.type !== "penthouse");
            const otherItems = (d.store || []).filter((it) => it.type === "penthouse");
            const selCredit = creditItems.find((it) => it.key === selectedCreditKey)
              || creditItems.find((it) => it.affordable) || creditItems[0];
            return (
              <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" variants={container}>
                {/* Consolidated shoot-credit card with value dropdown */}
                {selCredit && (
                  <motion.div variants={fadeUp} data-testid="store-credit-card"
                    animate={justRedeemed === selCredit.key ? { scale: [1, 1.05, 1], boxShadow: ["0 0 0 rgba(176,141,87,0)", "0 0 34px rgba(176,141,87,0.6)", "0 0 0 rgba(176,141,87,0)"] } : {}}
                    transition={{ duration: justRedeemed === selCredit.key ? 1.3 : 0.3, ease: "easeOut" }}
                    className={`relative overflow-hidden rounded-[5px] p-5 flex flex-col ${otherItems.length === 0 ? "md:col-span-2" : ""} ${selCredit.affordable ? "border border-[#B08D57]/45 bg-[#FBFAF7]" : "border border-[#2A0A0A]/8 bg-[#FAF9F6]"}`}>
                    <div className="flex items-center gap-2.5 relative">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#2E4F3B]/12 ${!selCredit.affordable ? "opacity-60" : ""}`}>
                        <CurrencyDollar size={18} weight="duotone" className="text-[#2E4F3B]" />
                      </div>
                      <div className="min-w-0">
                        <p className={`font-display text-lg ${selCredit.affordable ? "text-[#2A0A0A]" : "text-[#5C5A58]"}`}>Shoot credit</p>
                        <p className="text-[11px] text-[#9A968E]">Redeem points for account credit</p>
                      </div>
                    </div>
                    <p className="text-xs text-[#5C5A58] mt-3 leading-relaxed">{selCredit.desc}</p>
                    <div className="mt-3">
                      <label className="text-[11px] uppercase tracking-[0.14em] text-[#9A968E]">Choose credit amount</label>
                      <Select value={selCredit.key} onValueChange={setSelectedCreditKey}>
                        <SelectTrigger data-testid="credit-amount-select" className="mt-1.5 bg-white border-[#B08D57]/40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {creditItems.map((it) => (
                            <SelectItem key={it.key} value={it.key} data-testid={`credit-option-${it.key}`}>
                              {it.label} — {it.points.toLocaleString()} pts{!it.affordable ? " (locked)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className="font-mono tabular-nums text-sm font-semibold text-[#4A0000]">{selCredit.points.toLocaleString()} <span className="text-[#9A968E] font-sans font-normal">pts</span></span>
                      <Button size="sm" data-testid="credit-redeem-btn" disabled={!selCredit.affordable || redeeming === selCredit.key}
                        onClick={() => setConfirmItem(selCredit)}
                        className={selCredit.affordable ? "bg-[#4A0000] hover:bg-[#5C1A1A] text-white" : "bg-transparent border border-[#2A0A0A]/15 text-[#9A968E] hover:bg-transparent cursor-not-allowed"}>
                        {redeeming === selCredit.key ? "Redeeming…" : selCredit.affordable ? `Redeem ${selCredit.label}` : <><Lock size={13} weight="fill" className="mr-1" />Locked</>}
                      </Button>
                    </div>
                    {!selCredit.affordable && <p className="text-[10px] text-[#9A968E] mt-2 font-mono tabular-nums">{(selCredit.points - d.points).toLocaleString()} more pts to unlock this amount</p>}
                  </motion.div>
                )}

                {/* Non-credit rewards (e.g. penthouse) keep their own cards */}
                {otherItems.map((it) => {
                  const celeb = justRedeemed === it.key;
                  return (
                    <motion.div key={it.key} variants={fadeUp} data-testid={`store-item-${it.key}`}
                      whileHover={it.affordable ? { y: -5 } : {}}
                      animate={celeb ? { scale: [1, 1.05, 1], boxShadow: ["0 0 0 rgba(176,141,87,0)", "0 0 34px rgba(176,141,87,0.6)", "0 0 0 rgba(176,141,87,0)"] } : {}}
                      transition={{ duration: celeb ? 1.3 : 0.3, ease: "easeOut" }}
                      className={`relative overflow-hidden rounded-[5px] p-5 flex flex-col transition-shadow ${it.affordable ? "border border-[#B08D57]/45 bg-[#FBFAF7] hover:shadow-[0_10px_34px_rgba(74,0,0,0.12)]" : "border border-[#2A0A0A]/8 bg-[#FAF9F6]"}`}>
                      {it.affordable && <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-[#B08D57]/20 blur-2xl pointer-events-none" />}
                      <AnimatePresence>
                        {celeb && (
                          <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: [0, 1, 0], scale: 1.4 }} exit={{ opacity: 0 }} transition={{ duration: 1.2 }}
                            className="absolute right-3 top-3 pointer-events-none"><Sparkle size={22} weight="fill" className="text-[#B08D57]" /></motion.div>
                        )}
                      </AnimatePresence>
                      <div className="flex items-center gap-2.5 relative">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#B08D57]/18 ${!it.affordable ? "opacity-60" : ""}`}>
                          <Crown size={18} weight="fill" className="text-[#B08D57]" />
                        </div>
                        <p className={`font-display text-lg ${it.affordable ? "text-[#2A0A0A]" : "text-[#5C5A58]"}`}>{it.label}</p>
                      </div>
                      <p className="text-xs text-[#5C5A58] mt-2 flex-1 leading-relaxed">{it.desc}</p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="font-mono tabular-nums text-sm font-semibold text-[#4A0000]">{it.points.toLocaleString()} <span className="text-[#9A968E] font-sans font-normal">pts</span></span>
                        <Button size="sm" data-testid={`store-redeem-${it.key}`} disabled={!it.affordable || redeeming === it.key}
                          onClick={() => setConfirmItem(it)}
                          className={it.affordable ? "bg-[#4A0000] hover:bg-[#5C1A1A] text-white" : "bg-transparent border border-[#2A0A0A]/15 text-[#9A968E] hover:bg-transparent cursor-not-allowed"}>
                          {redeeming === it.key ? "Redeeming…" : it.affordable ? "Redeem" : <><Lock size={13} weight="fill" className="mr-1" />Locked</>}
                        </Button>
                      </div>
                      {!it.affordable && <p className="text-[10px] text-[#9A968E] mt-2 font-mono tabular-nums">{(it.points - d.points).toLocaleString()} more pts to unlock</p>}
                    </motion.div>
                  );
                })}
              </motion.div>
            );
          })()}
          {d.redemptions?.length > 0 && (
            <div className="pt-1">
              <p className="text-xs text-[#9A968E] mb-2 tracking-wide uppercase">Recent redemptions</p>
              <div className="rounded-[5px] border border-[#1A1715]/8 bg-[#FBFAF7] divide-y divide-[#F0ECE2]">
                {d.redemptions.slice(0, 5).map((x, i) => (
                  <div key={i} className="flex items-center justify-between text-sm px-4 py-2.5" data-testid={`redemption-${i}`}>
                    <span className="flex items-center gap-2 text-[#1A1715]">{x.type === "penthouse" ? <Crown size={14} weight="fill" className="text-[#B08D57]" /> : <CurrencyDollar size={14} weight="duotone" className="text-[#2E4F3B]" />}{x.label}</span>
                    <span className="text-[#9A968E] font-mono tabular-nums text-xs">−{x.points} pts · {x.at ? new Date(x.at).toLocaleDateString() : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      <Dialog open={!!confirmItem} onOpenChange={(o) => !o && !redeeming && setConfirmItem(null)}>
        <DialogContent data-testid="redeem-confirm-dialog">
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-[#B08D57]/15 flex items-center justify-center mb-1">
              {confirmItem?.type === "penthouse" ? <Crown size={26} weight="fill" className="text-[#B08D57]" /> : <Gift size={24} weight="duotone" className="text-[#B08D57]" />}
            </div>
            <DialogTitle className="font-display text-2xl text-[#4A0000]">Confirm redemption</DialogTitle>
            <DialogDescription>
              {confirmItem && <>Spend <span className="font-semibold text-[#4A0000]">{confirmItem.points.toLocaleString()} points</span> on <span className="font-semibold">{confirmItem.label}</span>? This deducts the points from your balance{confirmItem.type === "credit" ? " and adds the credit to your next shoot invoice" : confirmItem.type === "penthouse" ? " and unlocks your Penthouse term" : ". An admin will fulfill this reward shortly"}. This can't be undone.</>}}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="redeem-confirm-cancel" disabled={!!redeeming} onClick={() => setConfirmItem(null)}>Cancel</Button>
            <Button data-testid="redeem-confirm-yes" disabled={!!redeeming} onClick={() => doRedeem(confirmItem)} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{redeeming ? "Redeeming…" : "Yes, redeem"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Points breakdown */}
      {d.breakdown && d.breakdown.length > 0 && (
      <motion.div variants={fadeUp} className="space-y-3">
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58] flex items-center gap-1.5"><TrendUp size={16} weight="duotone" className="text-[#B08D57]" />How you earn points</p>
        <motion.div className="grid grid-cols-1 sm:grid-cols-3 gap-4" variants={container}>
          {d.breakdown.map((b) => (
            <motion.div variants={fadeUp} key={b.label} whileHover={{ y: -3 }} className="surface-kpi surface-kpi--emerald p-5" data-testid={`rewards-breakdown-${b.label}`}>
              <div className="flex items-center justify-between relative z-10">
                <p className="text-sm text-[#5C5A58]">{b.label}</p>
                <span className="h-8 w-8 rounded-full flex items-center justify-center border bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/30"><Sparkle size={14} weight="fill" /></span>
              </div>
              <p className="font-display text-3xl text-[#1A4331] tabular-nums mt-0.5 relative z-10">{b.points.toLocaleString()} <span className="text-sm text-[#9A968E] font-sans">pts</span></p>
              <p className="text-xs text-[#9A968E] mt-0.5 relative z-10">{b.count} × {b.each} pts each</p>
            </motion.div>
          ))}
          {d.bonus_points !== 0 && (
            <motion.div variants={fadeUp} whileHover={{ y: -3 }} className="surface-kpi surface-kpi--bronze p-5">
              <div className="flex items-center justify-between relative z-10">
                <p className="text-sm text-[#8A5028] flex items-center gap-1"><Sparkle size={14} weight="fill" />Bonus points</p>
                <span className="h-8 w-8 rounded-full flex items-center justify-center border bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/35"><Crown size={14} weight="fill" /></span>
              </div>
              <p className="font-display text-3xl text-[#8A5028] tabular-nums mt-0.5 relative z-10">{d.bonus_points > 0 ? "+" : ""}{d.bonus_points.toLocaleString()}</p>
              <p className="text-xs text-[#9A968E] mt-0.5 relative z-10">Awarded by the Reelty team</p>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
      )}

      {/* Achievements */}
      {d.achievements && d.achievements.length > 0 && (
      <motion.div variants={fadeUp} className="space-y-3">
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58] flex items-center gap-1.5"><Medal size={16} weight="duotone" className="text-[#B08D57]" />Achievements</p>
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" variants={container}>
          {d.achievements.map((a) => (
            <motion.div variants={fadeUp} key={a.key} data-testid={`rewards-ach-${a.key}`}
              whileHover={a.earned ? { y: -3 } : {}}
              className={`relative overflow-hidden rounded-[5px] p-4 flex items-start gap-3 transition-shadow ${a.earned ? "border border-[#B08D57]/45 bg-[#B08D57]/[0.08] hover:shadow-[0_8px_26px_rgba(176,141,87,0.18)]" : "border border-[#1A1715]/8 bg-[#FBFAF7]"}`}>
              {a.earned && <div className="absolute -right-6 -top-6 w-16 h-16 rounded-full bg-[#B08D57]/25 blur-xl pointer-events-none" />}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 relative ${a.earned ? "bg-[#B08D57]/25" : "bg-[#F0ECE2]"}`}>
                {a.earned ? <CheckCircle size={20} weight="fill" className="text-[#8a6d0a]" /> : <Lock size={18} weight="duotone" className="text-[#9A968E]" />}
              </div>
              <div className="min-w-0 flex-1 relative">
                <p className={`font-medium text-sm flex items-center gap-1 ${a.earned ? "text-[#4A0000]" : "text-[#5C5A58]"}`}>{a.earned && <Star size={12} weight="fill" className="text-[#B08D57]" />}{a.label}</p>
                <p className="text-xs text-[#9A968E]">{a.desc}</p>
                {!a.earned && (
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-[#F0ECE2] overflow-hidden">
                      <motion.div className="h-full rounded-full bg-[#B08D57]" initial={{ width: 0 }} whileInView={{ width: `${a.progress}%` }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut" }} />
                    </div>
                    <p className="text-[10px] text-[#9A968E] mt-1 font-mono tabular-nums">{a.value}/{a.target}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
      )}

      {/* Leaderboard — friendly competition */}
      <Leaderboard />
    </motion.div>
  );
}

function AdminRewards() {
  const [role, setRole] = useState("agent");
  const [tab, setTab] = useState("members");
  const [data, setData] = useState(null);
  const [row, setRow] = useState(null);
  const [pts, setPts] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [reds, setReds] = useState(null);
  const [undoRow, setUndoRow] = useState(null);
  const [undoReason, setUndoReason] = useState("");
  const [undoing, setUndoing] = useState(false);

  const load = () => client.get("/rewards/admin").then((r) => setData(r.data));
  const loadReds = () => client.get("/rewards/redemptions").then((r) => setReds(r.data)).catch(() => setReds({ rows: [] }));
  useEffect(() => { load(); loadReds(); }, []);

  const openAdjust = (r) => { setPts(""); setReason(""); setRow(r); };
  const save = async () => {
    const p = parseInt(pts, 10);
    if (!p) return toast.error("Enter a non-zero points value");
    if (!reason.trim()) return toast.error("A reason is required");
    setSaving(true);
    try {
      await client.post("/rewards/adjust", { user_id: row.user_id, points: p, reason: reason.trim() });
      toast.success(`${p > 0 ? "Added" : "Removed"} ${Math.abs(p)} pts for ${row.name}`);
      setRow(null);
      load(); loadReds();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not adjust points"); }
    finally { setSaving(false); }
  };

  const openUndo = (r) => { setUndoReason(""); setUndoRow(r); };
  const doUndo = async () => {
    if (!undoRow) return;
    setUndoing(true);
    try {
      const r = await client.post(`/rewards/redemptions/${undoRow.id}/undo`, { reason: undoReason.trim() || null });
      toast.success(r.data?.note || `Reversed ${undoRow.label} for ${undoRow.member}`);
      setUndoRow(null);
      loadReds(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not undo this redemption"); }
    finally { setUndoing(false); }
  };

  const doFulfill = async (r) => {
    try {
      await client.post(`/rewards/redemptions/${r.id}/fulfill`);
      toast.success(`Marked "${r.label}" fulfilled for ${r.member}`);
      loadReds();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark fulfilled"); }
  };

  if (!data) return <p className="text-[#5C5A58]">Loading…</p>;
  const money$ = (n) => `$${Number(n || 0).toFixed(2)}`;
  const roleRows = data.rows.filter((r) => r.role === role);
  const roleReds = (reds?.rows || []).filter((r) => r.role === role);
  const roleActive = roleReds.filter((r) => r.status !== "reversed");
  const STAT = [
    { label: "Active redemptions", val: roleActive.length, variant: "oxblood-accent", medal: "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/20", value: "#4A0000", icon: Gift },
    { label: "Pending fulfillment", val: roleReds.filter((r) => r.status === "pending").length, variant: "bronze", medal: "bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/35", value: "#8A5028", icon: Sparkle },
    { label: "Credit outstanding", val: money$(roleActive.filter((r) => r.type === "credit").reduce((s, r) => s + (r.value || 0), 0)), variant: "emerald", medal: "bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/30", value: "#1A4331", icon: CurrencyDollar },
    { label: "Reversed", val: roleReds.filter((r) => r.status === "reversed").length, variant: "sapphire", medal: "bg-[#E6EAEF] text-[#12233D] border-[#2B4C7E]/30", value: "#12233D", icon: ArrowCounterClockwise },
  ];

  return (
    <div className="space-y-6" data-testid="rewards-admin">
      {/* Role selector — each role has its own independent rewards program */}
      <div className="flex flex-wrap gap-2" data-testid="rewards-role-switch">
        {ROLE_TABS.map(([val, label]) => (
          <button key={val} data-testid={`rewards-role-${val}`} onClick={() => setRole(val)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${role === val ? "bg-[#4A0000] text-white shadow-[0_6px_18px_-6px_rgba(74,0,0,0.5)]" : "bg-[#FAF9F6] text-[#5C5A58] hover:bg-[#EEE8DA]"}`}>
            {label}
            <span className={`ml-1.5 text-xs ${role === val ? "text-white/60" : "text-[#9A968E]"}`}>{data.by_role?.[val] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 border-b border-[#E9DDC6]">
        <button data-testid="rewards-tab-members" onClick={() => setTab("members")}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "members" ? "border-[#4A0000] text-[#4A0000]" : "border-transparent text-[#5C5A58] hover:text-[#1A1715]"}`}>
          Members & points
        </button>
        <button data-testid="rewards-tab-redemptions" onClick={() => setTab("redemptions")}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${tab === "redemptions" ? "border-[#4A0000] text-[#4A0000]" : "border-transparent text-[#5C5A58] hover:text-[#1A1715]"}`}>
          <Gift size={15} weight="duotone" />Redemptions
          {reds?.active_count > 0 && <span className="text-[10px] bg-[#B08D57]/20 text-[#8a6d0a] rounded-full px-1.5 py-0.5 font-mono tabular-nums">{reds.active_count}</span>}
        </button>
        <button data-testid="rewards-tab-structure" onClick={() => setTab("structure")}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${tab === "structure" ? "border-[#4A0000] text-[#4A0000]" : "border-transparent text-[#5C5A58] hover:text-[#1A1715]"}`}>
          <SlidersHorizontal size={15} weight="duotone" />Structure
        </button>
      </div>

      {tab === "structure" && <RewardsStructure role={role} />}

      {tab === "members" && (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="lux-panel overflow-hidden">
        <div className="bg-[#FAF9F6] px-4 py-3 flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58]">
          <Trophy size={16} weight="duotone" className="text-[#B08D57]" />{ROLE_TABS.find(([v]) => v === role)?.[1] || role} — rewards & achievements
        </div>
        <table className="w-full text-sm" data-testid="rewards-admin-rows">
          <thead>
            <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
              <th className="p-4 font-medium">Member</th>
              <th className="p-4 font-medium">Level</th>
              <th className="p-4 font-medium">Points</th>
              <th className="p-4 font-medium">Achievements</th>
              <th className="p-4 font-medium text-right">Adjust</th>
            </tr>
          </thead>
          <tbody>
            {roleRows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#5C5A58]">No {role}s yet.</td></tr>}
            {roleRows.map((r, i) => (
              <tr key={r.user_id} className={`${i % 2 ? "bg-[#FBFAF7]" : "bg-white"} hover:bg-[#FAF9F6]/70 transition-colors`} data-testid={`rewards-admin-row-${r.user_id}`}>
                <td className="p-4 font-medium">{r.name}</td>
                <td className="p-4"><Badge className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">{r.level}</Badge></td>
                <td className="p-4 font-mono tabular-nums font-semibold text-[#4A0000]">{r.points.toLocaleString()}{r.bonus_points !== 0 && <span className="text-xs text-[#8a6d0a] font-normal"> ({r.bonus_points > 0 ? "+" : ""}{r.bonus_points} bonus)</span>}</td>
                <td className="p-4 font-mono tabular-nums">{r.earned_count}/{r.achievement_total}</td>
                <td className="p-4 text-right">
                  <Button data-testid={`rewards-adjust-${r.user_id}`} size="sm" variant="outline" className="border-[#E9DDC6]" onClick={() => openAdjust(r)}><PencilSimple size={15} className="mr-1" />Adjust</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>
      )}

      {tab === "redemptions" && (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="rewards-redemptions">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STAT.map((s) => (
            <div key={s.label} className={`surface-kpi surface-kpi--${s.variant} p-4`} data-testid={`rewards-stat-${s.label}`}>
              <div className="flex items-center justify-between relative z-10">
                <p className="text-xs text-[#5C5A58]">{s.label}</p>
                <span className={`h-8 w-8 rounded-full flex items-center justify-center border ${s.medal}`}><s.icon size={14} weight="duotone" /></span>
              </div>
              <p className="font-display text-2xl tabular-nums mt-1 relative z-10" style={{ color: s.value }}>{s.val}</p>
            </div>
          ))}
        </div>
        <div className="lux-panel overflow-hidden">
          <div className="bg-[#FAF9F6] px-4 py-3 flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58]"><Gift size={16} weight="duotone" className="text-[#B08D57]" />Point redemptions — 10 pts = $1</div>
          <table className="w-full text-sm" data-testid="redemptions-rows">
            <thead>
              <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
                <th className="p-4 font-medium">Member</th>
                <th className="p-4 font-medium">Reward</th>
                <th className="p-4 font-medium">Points</th>
                <th className="p-4 font-medium">Value</th>
                <th className="p-4 font-medium">When</th>
                <th className="p-4 font-medium text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {(!roleReds || roleReds.length === 0) && <tr><td colSpan={6} className="p-8 text-center text-[#5C5A58]">No redemptions yet.</td></tr>}
              {roleReds.map((r, i) => (
                <tr key={r.id} className={`${i % 2 ? "bg-[#FBFAF7]" : "bg-white"} ${r.status === "reversed" ? "opacity-60" : "hover:bg-[#FAF9F6]/70"} transition-colors align-top`} data-testid={`redemption-row-${r.id}`}>
                  <td className="p-4"><p className="font-medium text-[#1A1715]">{r.member}</p><p className="text-xs text-[#9A968E]">{r.email}</p></td>
                  <td className="p-4"><span className="flex items-center gap-1.5">{r.type === "penthouse" ? <Crown size={15} weight="fill" className="text-[#B08D57]" /> : <CurrencyDollar size={15} weight="duotone" className="text-[#2E4F3B]" />}{r.label}</span></td>
                  <td className="p-4 font-mono tabular-nums text-[#4A0000]">−{r.points.toLocaleString()}</td>
                  <td className="p-4 font-mono tabular-nums">{money$(r.value)}</td>
                  <td className="p-4 text-xs text-[#5C5A58]">{r.at ? new Date(r.at).toLocaleDateString() : ""}</td>
                  <td className="p-4 text-right">
                    {r.status === "reversed"
                      ? <div className="flex flex-col items-end gap-1">
                          <span className="pill pill--info">Reversed</span>
                          {r.reversed_reason && <span className="text-[10px] text-[#9A968E] italic max-w-[180px] text-right">“{r.reversed_reason}”</span>}
                          {r.reversed_by && <span className="text-[10px] text-[#C4BFB4]">by {r.reversed_by}</span>}
                        </div>
                      : <div className="flex items-center justify-end gap-2">
                          {r.status === "pending" && (
                            <Button data-testid={`redemption-fulfill-${r.id}`} size="sm" className="bg-[#2E4F3B] hover:bg-[#24402f] text-white" onClick={() => doFulfill(r)}><Check size={15} className="mr-1" weight="bold" />Mark fulfilled</Button>
                          )}
                          {r.status === "fulfilled" && (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="pill pill--paid">Fulfilled</span>
                              {r.fulfilled_by && <span className="text-[10px] text-[#C4BFB4]">by {r.fulfilled_by}</span>}
                            </div>
                          )}
                          <Button data-testid={`redemption-undo-${r.id}`} size="sm" variant="outline" className="border-[#E9DDC6] text-[#7E1119] hover:bg-[#7E1119]/5" onClick={() => openUndo(r)}><ArrowCounterClockwise size={15} className="mr-1" />Undo</Button>
                        </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
      )}

      <Dialog open={!!row} onOpenChange={(o) => !o && setRow(null)}>
        <DialogContent data-testid="rewards-adjust-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Adjust points</DialogTitle>
            <DialogDescription>{row?.name} — award or deduct bonus points. Use a negative value to deduct. A reason is required and logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-[#5C5A58] mb-1">Points (e.g. 100 or −50)</p>
              <Input data-testid="rewards-adjust-points" type="number" step="1" value={pts} onChange={(e) => setPts(e.target.value)} placeholder="100" />
            </div>
            <div>
              <p className="text-sm text-[#5C5A58] mb-1">Reason (required)</p>
              <Input data-testid="rewards-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. top performer this month, contest winner" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="rewards-adjust-cancel" onClick={() => setRow(null)}>Cancel</Button>
            <Button data-testid="rewards-adjust-save" disabled={saving} onClick={save} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save points"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!undoRow} onOpenChange={(o) => !o && !undoing && setUndoRow(null)}>
        <DialogContent data-testid="redemption-undo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#7E1119]">Undo redemption</DialogTitle>
            <DialogDescription>
              {undoRow && <>Reverse <span className="font-semibold">{undoRow.label}</span> for <span className="font-semibold">{undoRow.member}</span>? This refunds <span className="font-semibold">{undoRow.points.toLocaleString()} points</span>{undoRow.type === "credit" ? " and claws back the shoot credit (only the portion not yet applied to an invoice)" : " and rolls back their Penthouse term"}.</>}
            </DialogDescription>
          </DialogHeader>
          <div>
            <p className="text-sm text-[#5C5A58] mb-1">Reason (optional — shown in the history)</p>
            <Input data-testid="redemption-undo-reason" value={undoReason} onChange={(e) => setUndoReason(e.target.value)} placeholder="e.g. redeemed by mistake, duplicate, customer request" />
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="redemption-undo-cancel" disabled={undoing} onClick={() => setUndoRow(null)}>Cancel</Button>
            <Button data-testid="redemption-undo-confirm" disabled={undoing} onClick={doUndo} className="bg-[#7E1119] hover:bg-[#5f0d13] text-white">{undoing ? "Undoing…" : "Yes, undo"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function RewardsPanel() {
  const { user } = useAuth();
  return user.role === "admin" ? <AdminRewards /> : <MemberRewards />;
}
